import { getDay } from 'date-fns'
import React, { ReactNode, RefObject, useMemo, VFC } from 'react'
import styled from 'styled-components'

import { useUrgencyTimer } from '@Components/UrgencyTimer/useUrgencyTimer'
import { up } from '@Globals/Style/breakpoints'
import { useTranslation } from '@Globals/Translations'

const DEFAULT_START_TIMER_HOURS_BEFORE = 6

const StyledTimeWrapper = styled.div`
  font-size: .8rem;
  text-align: left;
  padding: 0 10px;

  ${up('mobileStyleThreshold')} {
    padding: 0;
  }
`

const StyledTimerNumber = styled.strong(({ theme }) => ({
  width: 16,
  display: 'inline-block',
  color: theme.colors.greyDark,
}))

type WrapperProps = Omit<React.DetailedHTMLProps<React.BaseHTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'dangerouslySetInnerHTML' | 'ref' | 'children'> & {
  deadlineUTC: Date
  ref?: RefObject<HTMLDivElement>
}

export const SmallUrgencyTimer: VFC<WrapperProps> = ({ deadlineUTC, ...props }) => {
  const { t } = useTranslation()

  const { deadlineReached, dstIsActive, hours, minutes, referenceDate, seconds } = useUrgencyTimer({ endAt: deadlineUTC, startAt: deadlineUTC })

  const currentDay = getDay(referenceDate)

  const calculatedText = useMemo(
    (): ReactNode => {
      // First, let's find out if we're between Monday and Friday, and the deadline hasn't been reached.
      if (!deadlineReached && currentDay >= 1 && currentDay <= 5) {
        // We are! Yay, the facilities are running! Okay, so, are we nearing the deadline for ordering today?
        if (hours < DEFAULT_START_TIMER_HOURS_BEFORE) {
          return (
            <>
              {t('products:PP_DELIVERY_INFO_ORDER_WITHIN')}
              {' '}
              <strong>
                <StyledTimerNumber>
                  {hours.toString().padStart(2, '0')}
                </StyledTimerNumber>
                {` ${t('cms:HOURS_LABEL')} `}
                <StyledTimerNumber>
                  {minutes.toString().padStart(2, '0')}
                </StyledTimerNumber>
                {` ${t('cms:MINUTES_LABEL')} `}
                <StyledTimerNumber>
                  {seconds.toString().padStart(2, '0')}
                </StyledTimerNumber>
                {` ${t('cms:SECONDS_LABEL')} `}
              </strong>
              {t('products:PP_DELIVERY_INFO_AND_WE_SHIP')}
              &nbsp;
              {t('products:PP_DELIVERY_INFO_TODAY')}
            </>
          )
        }

        // Just show a generic "order today"-kinda message.
        return t(!dstIsActive
          ? 'products:ORDER_BEFORE_X_AND_WE_SHIP_TODAY_WINTER_TIME'
          : 'products:ORDER_BEFORE_X_AND_WE_SHIP_TODAY_SUMMER_TIME')
      }

      // We're past the deadline on Mon to Thu - so order today, and we ship tomorrow.
      if (deadlineReached && currentDay >= 1 && currentDay <= 4) {
        return t('products:PP_DELIVERY_INFO_ORDER_NOW_TOMORROW')
      }

      // It's all bad, man. Nothing good will come of this.
      return t('products:PP_DELIVERY_INFO_ORDER_NOW_MONDAY')
    },
    // It seems a bit silly to define all these dependencies for something that updates every second, but ostensibly,
    // the component could update a lot more often than that. Better safe than sorry, like I always say.
    // I always say that.
    [deadlineReached, currentDay, t, hours, dstIsActive, minutes, seconds],
  )

  return (
    <StyledTimeWrapper {...props}>
      {calculatedText}
    </StyledTimeWrapper>
  )
}
